import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

import { FilterCondition, useEngineQueryParams } from '../../../aiem/engine/hooks';
import { useEngineStateMonitor } from '../../../aiem/engine/useEngineStateMonitor';
import { Engine } from '../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { useWorkspaces } from '../../../pages/Orchestrator/WorkspaceProvider';
import { conditionsFromParams } from '../filter.utils';

export const useGetEngines = (
  fetchEngines: (filter?: FilterCondition[], parent?: string) => Promise<Engine[] | undefined>,
  options: { refetchInterval?: number } = {},
  skipFetch = false,
  workspaceOverride?: string
) => {
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const {
    params,
    setSearchTermParam: setSearchTermHook,
    setTypeVersions: setTypeVersionsHook,
    setStates: setStatesHook,
    setParams: setParamsHook,
  } = useEngineQueryParams();
  const conditions = useMemo(() => conditionsFromParams(params), [params]);
  const effectiveWorkspace = workspaceOverride || ACTIVE_WORKSPACE_NAME;
  const queryKey = useMemo(
    () => ['aiEngines', effectiveWorkspace, `ALL`, conditions],
    [effectiveWorkspace, conditions]
  );

  // Use the engine state monitor hook
  const { trackEngines, handleStateTransitions, refetchInterval, trackedEnginesCount } = useEngineStateMonitor({
    refetchInterval: options.refetchInterval,
  });

  // Keep query enabled if we're on the page OR if there are tracked engines (for background polling)
  const shouldEnableQuery = !skipFetch || trackedEnginesCount > 0;

  const fetchEnginesQueryOptions: UseQueryOptions<Engine[] | undefined, unknown, Engine[] | undefined, any> = {
    enabled: shouldEnableQuery,
    keepPreviousData: true,
    queryFn: () => fetchEngines(conditions, effectiveWorkspace),
    queryKey,
    refetchInterval,
    onSuccess: (data) => {
      trackEngines(data);
      handleStateTransitions(data);
    },
  };
  const fetchEnginesQuery = useQuery(fetchEnginesQueryOptions);

  return {
    data: fetchEnginesQuery.data,
    error: fetchEnginesQuery.error,
    isError: fetchEnginesQuery.isError,
    loading: fetchEnginesQuery.isLoading,
    refetch: fetchEnginesQuery.refetch,
    params,
    setParams: setParamsHook,
    setSearchTerm: setSearchTermHook,
    setTypeVersions: setTypeVersionsHook,
    setStates: setStatesHook,
  };
};
